import * as authTypes from '@redux/types/auth';
import localforage from 'localforage';
import forEach from 'lodash/forEach';
import omit from 'lodash/omit';
import union from 'lodash/union';
import uniq from 'lodash/uniq';
import { combineReducers } from 'redux';
import { persistReducer } from 'redux-persist';
import autoMergeLevel2 from 'redux-persist/lib/stateReconciler/autoMergeLevel2';
import * as types from '../types';

const byId = (state = {}, action) => {
  switch (action.type) {
    case authTypes.AUTHENTICATION_IDENTITY_CLEARED: {
      return {};
    }
    case types.PROTOCOLS_FETCH_COMPLETED: {
      const { entities, order } = action.payload;
      const newState = {};
      order.forEach((protocolId) => {
        newState[protocolId] = {
          isSelected: false,
          ...state[protocolId],
          ...entities[protocolId],
          isConfirmed: true
        };
      });
      return newState;
    }
    case types.PROTOCOLS_PARTIAL_FETCH_COMPLETED: {
      const { entities, order } = action.payload;
      const newState = { ...state };
      order.forEach((protocolId) => {
        newState[protocolId] = {
          isSelected: false,
          ...state[protocolId],
          ...entities[protocolId],
          isConfirmed: true
        };
      });
      return newState;
    }

    case types.PROTOCOLS_ADD_COMPLETED: {
      const { oldId, protocol } = action.payload;
      const newState = omit(state, oldId);
      newState[protocol.protocolId] = {
        ...state[oldId],
        ...protocol,
        isConfirmed: true
      };
      return newState;
    }

    case types.PROTOCOLS_EDIT_COMPLETED: {
      const protocol = action.payload;
      return {
        ...state,
        [protocol.protocolId]: {
          ...omit(state[protocol.protocolId], ['oldPROTOCOLS']),
          ...omit(protocol, ['oldPROTOCOLS']),
          isConfirmed: true
        }
      };
    }

    //TYPES STARTED ADD AND EDIT
    case types.PROTOCOLS_ADD_STARTED: {
      const protocol = action.payload;
      const newState = { ...state };
      newState[protocol.protocolId] = {
        isSelected: false,
        isConfirmed: false
      };
      return newState;
    }
    case types.PROTOCOLS_EDIT_STARTED: {
      const protocol = action.payload;
      return {
        ...state,
        [protocol.protocolId]: {
          ...state[protocol.protocolId],
          oldProtocol: state[protocol.protocolId],
          isConfirmed: false
        }
      };
    }

    //TYPES FAILED ADD AND EDIT
    case types.PROTOCOLS_ADD_FAILED: {
      const { oldId } = action.payload;
      if (state[oldId]) {
        return omit(state, oldId);
      }
      return state;
    }
    case types.PROTOCOLS_EDIT_FAILED: {
      const { oldProtocol } = action.payload;
      return {
        ...state,
        [oldProtocol.protocolId]: {
          ...omit(state[oldProtocol.protocolId], ['oldPROTOCOLS']),
          ...oldProtocol,
          isConfirmed: true
        }
      };
    }

    //TYPES REMOVE COMPLETED
    case types.PROTOCOLS_REMOVE_COMPLETED: {
      const { protocolId } = action.payload;
      return omit(state, protocolId);
    }

    //TYPES SELECTED AND DESELECTED
    case types.PROTOCOLS_SELECTED: {
      const protocolId = action.payload;
      const newState = {
        ...state,
        [protocolId]: {
          ...state[protocolId],
          isSelected: true
        }
      };
      return newState;
    }

    case types.PROTOCOLS_DESELECTED: {
      const protocolId = action.payload;
      return {
        ...state,
        [protocolId]: {
          ...state[protocolId],
          isSelected: false
        }
      };
    }

    case types.PROTOCOLS_ALL_SELECTED: {
      const protocolIds = action.payload;
      const newState = { ...state };
      if (protocolIds.length == 0) {
        forEach(state, (protocol: any, protocolId) => {
          newState[protocolId] = {
            ...protocol,
            isSelected: true
          };
        });
      } else {
        protocolIds.forEach((protocolId) => {
          newState[protocolId] = {
            ...state[protocolId],
            isSelected: true
          };
        });
      }

      return newState;
    }

    case types.PROTOCOLS_ALL_DESELECTED: {
      const protocolIds = action.payload;
      const newState = { ...state };
      if (protocolIds.length == 0) {
        forEach(state, (protocol: any, protocolId) => {
          newState[protocolId] = {
            ...protocol,
            isSelected: false
          };
        });
      } else {
        protocolIds.forEach((protocolId) => {
          newState[protocolId] = {
            ...state[protocolId],
            isSelected: false
          };
        });
      }

      return newState;
    }

    default: {
      return state;
    }
  }
};

const order = (state = [], action) => {
  switch (action.type) {
    case authTypes.AUTHENTICATION_IDENTITY_CLEARED: {
      return [];
    }
    //CASE COMPLETED FETCH, ADD AND REMOVE
    case types.PROTOCOLS_FETCH_COMPLETED: {
      const { order } = action.payload;
      return union(order);
    }

    case types.PROTOCOLS_PARTIAL_FETCH_COMPLETED: {
      const { order } = action.payload;
      return uniq([...state, ...order]);
    }

    case types.PROTOCOLS_ADD_COMPLETED: {
      const { oldId, protocol } = action.payload;
      return state.map((protocolId) => (protocolId === oldId ? protocol.protocolId : protocolId));
    }

    case types.PROTOCOLS_REMOVE_COMPLETED: {
      const { protocolId } = action.payload;
      return state.filter((protocolIdState) => protocolIdState !== protocolId);
    }

    //CASE ADD STARTED
    case types.PROTOCOLS_ADD_STARTED: {
      const protocol = action.payload;
      return [...state, protocol.protocolId];
    }

    //CASE ADD FAILED
    case types.PROTOCOLS_ADD_FAILED: {
      const { oldId } = action.payload;
      return state.filter((protocolIdState) => protocolIdState !== oldId);
    }

    //DEFAULT
    default: {
      return state;
    }
  }
};

// STATES TO KNOW IF IT IS FETCHING, ADDING  OR EDITING
const isFetchingList = (state = false, action) => {
  switch (action.type) {
    case authTypes.AUTHENTICATION_IDENTITY_CLEARED: {
      return false;
    }
    case types.PROTOCOLS_FETCH_STARTED: {
      return true;
    }
    case types.PROTOCOLS_FETCH_COMPLETED:
    case types.PROTOCOLS_FETCH_FAILED: {
      return false;
    }
    default: {
      return state;
    }
  }
};

const isPartialFetchingList = (state = false, action) => {
  switch (action.type) {
    case authTypes.AUTHENTICATION_IDENTITY_CLEARED: {
      return false;
    }
    case types.PROTOCOLS_PARTIAL_FETCH_STARTED: {
      return true;
    }
    case types.PROTOCOLS_PARTIAL_FETCH_COMPLETED:
    case types.PROTOCOLS_PARTIAL_FETCH_FAILED: {
      return false;
    }
    default: {
      return state;
    }
  }
};

const isAdding = (state = false, action) => {
  switch (action.type) {
    case authTypes.AUTHENTICATION_IDENTITY_CLEARED: {
      return false;
    }
    case types.PROTOCOLS_ADD_STARTED: {
      return true;
    }
    case types.PROTOCOLS_ADD_COMPLETED:
    case types.PROTOCOLS_ADD_FAILED: {
      return false;
    }
    default: {
      return state;
    }
  }
};

const isEditing = (state = false, action) => {
  switch (action.type) {
    case authTypes.AUTHENTICATION_IDENTITY_CLEARED: {
      return false;
    }
    case types.PROTOCOLS_EDIT_STARTED: {
      return true;
    }
    case types.PROTOCOLS_EDIT_COMPLETED:
    case types.PROTOCOLS_EDIT_FAILED: {
      return false;
    }
    default: {
      return state;
    }
  }
};

const isRemoving = (state = false, action) => {
  switch (action.type) {
    case authTypes.AUTHENTICATION_IDENTITY_CLEARED: {
      return false;
    }
    case types.PROTOCOLS_REMOVE_STARTED: {
      return true;
    }
    case types.PROTOCOLS_REMOVE_COMPLETED:
    case types.PROTOCOLS_REMOVE_FAILED: {
      return false;
    }
    default: {
      return state;
    }
  }
};

const errorFetchingList = (state = null, action) => {
  switch (action.type) {
    case authTypes.AUTHENTICATION_IDENTITY_CLEARED: {
      return null;
    }
    case types.PROTOCOLS_FETCH_FAILED: {
      const { error } = action.payload;
      return error;
    }
    case types.PROTOCOLS_FETCH_STARTED:
    case types.PROTOCOLS_FETCH_COMPLETED: {
      return null;
    }
    default: {
      return state;
    }
  }
};

const errorPartialFetchingList = (state = null, action) => {
  switch (action.type) {
    case authTypes.AUTHENTICATION_IDENTITY_CLEARED: {
      return null;
    }
    case types.PROTOCOLS_PARTIAL_FETCH_FAILED: {
      const { error } = action.payload;
      return error;
    }
    case types.PROTOCOLS_PARTIAL_FETCH_STARTED:
    case types.PROTOCOLS_PARTIAL_FETCH_COMPLETED: {
      return null;
    }
    default: {
      return state;
    }
  }
};

const errorAdding = (state = null, action) => {
  switch (action.type) {
    case authTypes.AUTHENTICATION_IDENTITY_CLEARED: {
      return null;
    }
    case types.PROTOCOLS_ADD_FAILED: {
      const { error } = action.payload;
      return error;
    }
    case types.PROTOCOLS_ADD_STARTED:
    case types.PROTOCOLS_ADD_COMPLETED: {
      return null;
    }
    default: {
      return state;
    }
  }
};

const errorEditing = (state = null, action) => {
  switch (action.type) {
    case authTypes.AUTHENTICATION_IDENTITY_CLEARED: {
      return null;
    }
    case types.PROTOCOLS_EDIT_FAILED: {
      const { error } = action.payload;
      return error;
    }
    case types.PROTOCOLS_EDIT_STARTED:
    case types.PROTOCOLS_EDIT_COMPLETED: {
      return null;
    }
    default: {
      return state;
    }
  }
};

const errorRemoving = (state = null, action) => {
  switch (action.type) {
    case authTypes.AUTHENTICATION_IDENTITY_CLEARED: {
      return null;
    }
    case types.PROTOCOLS_REMOVE_FAILED: {
      const { error } = action.payload;
      return error;
    }
    case types.PROTOCOLS_REMOVE_STARTED:
    case types.PROTOCOLS_REMOVE_COMPLETED: {
      return null;
    }
    default: {
      return state;
    }
  }
};

const hasAlreadyFetched = (state = false, action) => {
  switch (action.type) {
    case authTypes.AUTHENTICATION_IDENTITY_CLEARED: {
      return false;
    }
    case types.PROTOCOLS_FETCH_COMPLETED: {
      return true;
    }
    default: {
      return state;
    }
  }
};

const protocols: any = combineReducers({
  byId,
  order,
  isPartialFetchingList,
  isFetchingList,
  isAdding,
  isEditing,
  isRemoving,
  errorPartialFetchingList,
  errorFetchingList,
  errorAdding,
  errorEditing,
  errorRemoving,
  hasAlreadyFetched
});

const protocolsPersistedConfig = {
  key: 'protocols',
  storage: localforage,
  stateReconciler: autoMergeLevel2,
  blacklist: ['isAdding', 'isEditing', 'isRemoving', 'isFetchingList', 'isPartialFetchingList'],
  whitelist: ['byId', 'order']
  // blackList: [],
};

export default persistReducer(protocolsPersistedConfig, protocols);
// export default drivers;

export const getOwnState = (state) => state.protocols;

export const getProtocol = (state, protocolId): types.Protocol | null =>
  getOwnState(state).byId[protocolId] ?? null;

export const getProtocolsList = (state): types.Protocol[] =>
  getOwnState(state).order.map((id) => getProtocol(state, id));

export const getProtocolsListByMembership = (state, membership) => {
  return getProtocolsList(state).filter((protocol) => {
    // Si no hay membership, no se filtra nada
    if (!membership) return true;

    // 1. El operador es obligatorio - debe coincidir siempre
    if (membership.operatorId && membership.operatorId !== protocol.operatorId) {
      return false;
    }

    // 2. Organización es opcional - incluir si protocolo tiene null/undefined O si coincide con membership
    if (membership.organizationId) {
      const protocolHasNoOrganization = !protocol.organizationId;
      const organizationMatches = membership.organizationId === protocol.organizationId;
      if (!protocolHasNoOrganization && !organizationMatches) {
        return false;
      }
    }

    // 3. División es opcional - incluir si protocolo tiene null/undefined O si coincide con membership
    if (membership.divisionId) {
      const protocolHasNoDivision = !protocol.divisionId;
      const divisionMatches = membership.divisionId === protocol.divisionId;
      if (!protocolHasNoDivision && !divisionMatches) {
        return false;
      }
    }

    // 4. Subdivisión es opcional - incluir si protocolo tiene null/undefined O si coincide con membership
    if (membership.subdivisionId) {
      const protocolHasNoSubdivision = !protocol.subdivisionId;
      const subdivisionMatches = membership.subdivisionId === protocol.subdivisionId;
      if (!protocolHasNoSubdivision && !subdivisionMatches) {
        return false;
      }
    }

    return true;
  });
};

export const getProtocolsListByMembershipAndEvents = (state, membership, eventIds) => {
  let protocols = getProtocolsListByMembership(state, membership);

  if (!Array.isArray(protocols)) {
    return [];
  }

  if (!eventIds || eventIds.length === 0) {
    console.log('[getProtocolsListByMembershipAndEvents] Sin eventIds, retornando todos:', protocols.length);
    return protocols;
  }

  console.log('[getProtocolsListByMembershipAndEvents] Filtrando por eventIds:', eventIds);
  const filtered = protocols.filter((protocol) => {
    const eventIdsField = (protocol as any).eventIds;
    if (!eventIdsField) {
      console.log('[getProtocolsListByMembershipAndEvents] Protocolo sin eventIds:', protocol.protocolId);
      return false;
    }

    // Convertir string a array de números únicos
    const protocolEventIds = Array.from(
      new Set(eventIdsField.split(',').map((id) => parseInt(id.trim(), 10)))
    );

    const passes = protocolEventIds.some((id) => eventIds.includes(id));
    if (!passes) {
      console.log(`[getProtocolsListByMembershipAndEvents] Protocolo ${protocol.protocolId} (${protocol.protocolName}) NO pasa. Sus eventos: [${protocolEventIds}], buscados: [${eventIds}]`);
    }
    return passes;
  });
  console.log('[getProtocolsListByMembershipAndEvents] Resultado:', filtered.length, 'de', protocols.length);
  return filtered;
};

//Status of sagas
export const isPartialFetchingListProtocols = (state) => getOwnState(state).isPartialFetchingList;
export const isFetchingListProtocols = (state) => getOwnState(state).isFetchingList;
export const haveProtocolsBeenFetched = (state) => getOwnState(state).hasAlreadyFetched;
export const isAddingProtocol = (state) => getOwnState(state).isAdding;
export const isEditingProtocol = (state) => getOwnState(state).isEditing;
export const isRemovingProtocol = (state) => getOwnState(state).isRemoving;
export const isCopyingProtocol = (state) => getOwnState(state).isCopying;

//Errors
export const getPartialFetchingListProtocolsErrors = (state) =>
  getOwnState(state).errorPartialFetchingList;
export const getFetchingListProtocolsErrors = (state) => getOwnState(state).errorFetchingList;
export const getAddingProtocolErrors = (state) => getOwnState(state).errorAdding;
export const getEditingProtocolErrors = (state) => getOwnState(state).errorEditing;
export const getRemovingProtocolErrors = (state) => getOwnState(state).errorRemoving;
